/**
 * Timezone Utilities for Analytics
 *
 * This file provides timezone-aware date handling utilities for analytics calculations.
 * It ensures consistent date conversions and period calculations across different timezones.
 *
 * Main functions:
 * - createTimezoneAwareDate: Converts dates to timezone-aware format with proper start/end of day handling
 * - getPeriod: Calculates comparison periods (previous period, last year, weekly, monthly) with timezone awareness
 */

import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  interval,
  startOfMonth,
  startOfWeek,
  subDays,
  subYears,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

// DateRange type matching the one defined in StatsFiltersContext.tsx
type DateRange = { from: Date; to: Date };

/**
 * Helper to properly handle timezone conversions
 *
 * @param date - The date to convert (Date, string, or number)
 * @param timezone - The timezone to use (e.g., 'America/New_York', 'UTC')
 * @param endOfDay - Whether to set the time to end of day (23:59:59.999) or start of day (00:00:00.000)
 * @returns A Date object converted from the specified timezone to UTC
 */
export const createTimezoneAwareDate = (date: Date | string | number, timezone: string, endOfDay = false): Date => {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const time = endOfDay ? '23:59:59.999' : '00:00:00.000';

  return fromZonedTime(`${year}-${month}-${day}T${time}`, timezone);
};

/**
 * Helper function for calculating periods with timezone awareness
 *
 * @param dateRange - The base date range to calculate periods from
 * @param value - The type of period comparison ('previous-period', 'last-year', 'weekly', 'monthly', 'daily', 'no-comparison')
 * @param timezone - The timezone to use for calculations (defaults to 'UTC')
 * @returns A DateRange for the comparison period, or undefined if no comparison is needed
 */
export const getPeriod = (dateRange: DateRange | undefined, value: string, timezone = 'UTC'): DateRange | undefined => {
  if (!dateRange) return undefined;

  // Convert UTC dates to timezone-aware dates for calculations
  const from = toZonedTime(dateRange.from, timezone) as Date;
  const to = toZonedTime(dateRange.to, timezone) as Date;

  switch (value) {
    case 'previous-period': {
      const datesInterval = interval(from, to);
      const numberOfDaysBetween = eachDayOfInterval(datesInterval).length;
      const previousPeriodTo = subDays(from, 1);
      const previousPeriodFrom = subDays(previousPeriodTo, numberOfDaysBetween);
      return {
        from: createTimezoneAwareDate(previousPeriodFrom, timezone),
        to: createTimezoneAwareDate(previousPeriodTo, timezone, true)
      };
    }

    case 'last-year': {
      return {
        from: createTimezoneAwareDate(subYears(from, 1), timezone),
        to: createTimezoneAwareDate(subYears(to, 1), timezone, true)
      };
    }

    case 'weekly': {
      return {
        from: createTimezoneAwareDate(startOfWeek(from, { weekStartsOn: 1 }), timezone),
        to: createTimezoneAwareDate(endOfWeek(to, { weekStartsOn: 1 }), timezone, true)
      };
    }

    case 'monthly': {
      return {
        from: createTimezoneAwareDate(startOfMonth(from), timezone),
        to: createTimezoneAwareDate(endOfMonth(to), timezone, true)
      };
    }

    case 'daily':
    case 'no-comparison':
      return undefined;
  }
};
